import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { CONSENT_CHANGED_EVENT, readConsent } from "@/lib/consent";

const GoogleTagManager = lazy(() =>
  import("@/components/GoogleTagManager").then((m) => ({ default: m.GoogleTagManager })),
);
const GoogleAnalytics = lazy(() =>
  import("@/components/GoogleAnalytics").then((m) => ({ default: m.GoogleAnalytics })),
);

/** Best-effort removal of the _ga cookies after the visitor withdraws consent. */
function removeGoogleAnalyticsCookies() {
  const host = window.location.hostname;
  const domains = ["", host, "." + host, "." + host.split(".").slice(-2).join(".")];
  document.cookie
    .split(";")
    .map((c) => c.trim().split("=")[0] ?? "")
    .filter((name) => name.startsWith("_ga"))
    .forEach((name) => {
      domains.forEach((domain) => {
        document.cookie = `${name}=; Max-Age=0; path=/${domain ? `; domain=${domain}` : ""}`;
      });
    });
}

export const GTM_ID = "GTM-WWZKBMQZ";
export const GA_TAG_ID = "GT-MK4665QJ";
export const GA_MEASUREMENT_ID = "G-54KS6GQ1BY";

/**
 * Loads Google Tag Manager and Google Analytics only when the visitor has accepted
 * statistics cookies. Nothing from Google is requested before that. Mounted once in the
 * root layout, so it works on every page.
 */
export const AnalyticsLoader = () => {
  const [enabled, setEnabled] = useState(false);
  const loadedRef = useRef(false);

  useEffect(() => {
    const stored = readConsent();
    if (stored === "true") {
      loadedRef.current = true;
      setEnabled(true);
    } else if (stored === "false") {
      // Declined earlier: make sure no Google Analytics cookie is left over.
      removeGoogleAnalyticsCookies();
    }
    const onChange = (event: Event) => {
      const value = (event as CustomEvent<"true" | "false">).detail;
      if (value === "true") {
        loadedRef.current = true;
        setEnabled(true);
        return;
      }
      // Declined: tell Google to stop and, if the scripts were already running, reload
      // the page so no further request leaves the browser.
      if (typeof window.gtag === "function") {
        window.gtag("consent", "update", { analytics_storage: "denied" });
      }
      removeGoogleAnalyticsCookies();
      if (loadedRef.current) {
        window.location.reload();
      } else {
        setEnabled(false);
      }
    };
    window.addEventListener(CONSENT_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(CONSENT_CHANGED_EVENT, onChange);
  }, []);

  if (!enabled) return null;

  return (
    <Suspense fallback={null}>
      <GoogleTagManager gtmId={GTM_ID} />
      <GoogleAnalytics gaId={GA_TAG_ID} additionalIds={[GA_MEASUREMENT_ID]} />
    </Suspense>
  );
};
